import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { FilterChipDef } from "./filterChipDefs";

export interface PopoverAnchorState {
  readonly chipDef: FilterChipDef;
  readonly anchorEl: HTMLElement;
}

interface PopoverAnchorContextValue {
  readonly state: PopoverAnchorState | null;
  readonly open: (chipDef: FilterChipDef, anchorEl: HTMLElement) => void;
  readonly close: () => void;
}

const Context = createContext<PopoverAnchorContextValue | null>(null);

export function PopoverAnchorProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<PopoverAnchorState | null>(null);
  const open = useCallback(
    (chipDef: FilterChipDef, anchorEl: HTMLElement) =>
      setState({ chipDef, anchorEl }),
    [],
  );
  const close = useCallback(() => setState(null), []);
  const value = useMemo(() => ({ state, open, close }), [state, open, close]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function usePopoverAnchor(): PopoverAnchorContextValue {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error("usePopoverAnchor must be used within PopoverAnchorProvider");
  }
  return ctx;
}
